import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Settings, Save } from 'lucide-react';

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/admin/platform-settings');
        setSettings(res.data);
      } catch { toast.error('Failed to load settings'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/platform-settings', settings);
      setSettings(res.data);
      toast.success('Settings saved successfully');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: Number(value) }));
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;

  // Adapt to real backend field names
  const fields = [
    { key: 'platform_fee_cents', label: 'Platform Fee (cents)', desc: 'Flat per-order platform charge' },
    { key: 'service_fee_cents', label: 'Service Fee (cents)', desc: 'Service charge per order' },
    { key: 'delivery_base_fee_cents', label: 'Delivery Base Fee (cents)', desc: 'Base delivery fee' },
    { key: 'delivery_per_mile_rate_cents', label: 'Delivery Per Mile (cents)', desc: 'Additional fee per mile', fallbackKey: 'delivery_per_mile_cents' },
    { key: 'driver_base_pay_cents', label: 'Driver Base Pay (cents)', desc: 'Base driver compensation' },
    { key: 'driver_per_mile_rate_cents', label: 'Driver Per Mile (cents)', desc: 'Per-mile driver pay', fallbackKey: 'driver_per_mile_cents' },
    { key: 'cancellation_fee_cents', label: 'Cancellation Fee (cents)', desc: 'Late cancellation penalty' },
    { key: 'default_vendor_commission_rate', label: 'Vendor Commission Rate', desc: 'Platform commission (0.15 = 15%)', fallbackKey: 'commission_rate' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Platform Settings</h1>
        <p className="text-muted-foreground mt-1">Configure fees, commissions, and platform parameters.</p>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Settings className="h-4 w-4" /> Fee Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fields.map(field => {
              const val = settings?.[field.key] ?? settings?.[field.fallbackKey] ?? '';
              return (
                <div key={field.key} className="space-y-2">
                  <Label className="text-sm font-medium">{field.label}</Label>
                  <Input
                    type="number"
                    step={field.key.includes('commission') || field.key.includes('rate') ? '0.01' : '1'}
                    value={val}
                    onChange={e => updateField(field.key, e.target.value)}
                    data-testid={`settings-${field.key}`}
                  />
                  <p className="text-xs text-muted-foreground">{field.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end mt-8 pt-4 border-t">
            <Button onClick={handleSave} disabled={saving} data-testid="settings-save-button">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
